import type { ProductStatus } from "@adpilot/shared";
import { IconAlertTriangle, IconBox, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { ProductThumbnail } from "@/components/shared/product-thumbnail";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTitle, PopoverTrigger } from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import type { CampaignCreationInvalidProduct } from "@/lib/campaign-creation-session";
import type { SelectedProduct } from "@/lib/campaign-creation-state";
import { AmazonProductLink, EligibilityCheckRetry, PaneHeading } from "./PickerParts";
import { invalidProductExplanations, matchesProductIssue } from "./product-listing-helpers";

function InlineProductIssue({ invalid }: { invalid: CampaignCreationInvalidProduct }) {
  const { t } = useTranslation("campaignCreation");
  const reasons = invalidProductExplanations(invalid, t);
  const singleReason = reasons.length === 1 ? reasons[0] : undefined;

  if (singleReason && singleReason.length <= 44) {
    return (
      <p role="alert" className="mt-1 flex items-start gap-1.5 text-xs text-muted-foreground">
        <IconAlertTriangle
          className="mt-0.5 size-3.5 shrink-0 text-destructive-foreground"
          aria-hidden
        />
        <span>{singleReason}</span>
      </p>
    );
  }

  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="xs"
            className="mt-1 text-destructive-foreground hover:text-destructive-foreground"
          />
        }
        aria-label={t("products.invalidDetails", { count: reasons.length })}
      >
        <IconAlertTriangle data-icon="inline-start" aria-hidden />
        <span>{t("products.invalidCount", { count: reasons.length })}</span>
      </PopoverTrigger>
      <PopoverContent align="start" side="top" className="w-72">
        <div className="flex flex-col gap-3">
          <PopoverTitle className="text-sm leading-5">
            {t("products.invalidPopoverTitle")}
          </PopoverTitle>
          <ul className="flex flex-col gap-1 text-xs text-muted-foreground">
            {reasons.map((reason) => (
              <li key={reason} className="flex gap-2">
                <span aria-hidden="true">•</span>
                <span>{reason}</span>
              </li>
            ))}
          </ul>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export interface AddedPaneProps {
  compactHeader?: boolean;
  selected: SelectedProduct[];
  productStatuses: Map<string, ProductStatus>;
  invalidProducts: CampaignCreationInvalidProduct[];
  marketplaceId: string;
  max: number;
  /** A vendor selection is keyed by ASIN; there is no SKU to show (#673). */
  vendor?: boolean;
  onRemove: (listingKeys: Iterable<string>) => void;
  onClear: () => void;
  onRetry: () => void;
  retryPending: boolean;
}

/** The selected products, with their validation issues and remove actions. */
export function AddedPane({
  compactHeader = false,
  selected,
  productStatuses,
  invalidProducts,
  marketplaceId,
  max,
  vendor = false,
  onRemove,
  onClear,
  onRetry,
  retryPending,
}: AddedPaneProps) {
  const { t } = useTranslation("campaignCreation");
  return (
    <section
      className="flex min-w-0 flex-col overflow-hidden rounded-[10px] border border-line bg-surface"
      aria-label={t("products.addedPane")}
    >
      <PaneHeading
        compact={compactHeader}
        title={t("products.added", { count: selected.length, max })}
      >
        {selected.length > 0 ? (
          <AlertDialog>
            <AlertDialogTrigger
              render={
                <Button
                  type="button"
                  variant="link"
                  size="xs"
                  className="-mr-1 px-1 text-destructive hover:text-destructive active:text-destructive data-pressed:text-destructive"
                />
              }
            >
              {t("actions.removeAll")}
            </AlertDialogTrigger>
            <AlertDialogPopup>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("products.removeAll.title")}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t("products.removeAll.description")}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogClose render={<Button variant="outline" />}>
                  {t("actions.cancel")}
                </AlertDialogClose>
                <AlertDialogClose render={<Button variant="destructive" onClick={onClear} />}>
                  {t("actions.remove")}
                </AlertDialogClose>
              </AlertDialogFooter>
            </AlertDialogPopup>
          </AlertDialog>
        ) : null}
      </PaneHeading>
      {selected.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center text-muted-foreground">
          <IconBox className="size-8" />
          <p className="text-xs">{t("products.emptyAdded")}</p>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto paper-scrollbar">
          {selected.map((product) => {
            const invalid = invalidProducts.find((candidate) =>
              matchesProductIssue(candidate, product),
            );
            return (
              <div
                key={product.listingKey}
                className={`flex items-center gap-3 border-b border-line p-4 last:border-b-0 ${invalid ? "bg-subtle/50" : ""}`}
              >
                <ProductThumbnail
                  imageUrl={product.imageUrl}
                  label={product.title ?? product.asin}
                  asin={product.asin}
                  sku={product.sku ?? undefined}
                  productStatus={productStatuses.get(product.listingKey)}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs">{product.title ?? product.asin}</p>
                  <p className="mt-0.5 flex min-w-0 flex-wrap items-center gap-1 font-mono text-xs leading-[18px] text-muted-foreground">
                    <span className="shrink-0">ASIN: {product.asin}</span>
                    <AmazonProductLink asin={product.asin} marketplaceId={marketplaceId} />
                    {vendor || !product.sku ? null : (
                      <>
                        <span aria-hidden="true">|</span>
                        <span className="truncate">SKU: {product.sku}</span>
                      </>
                    )}
                  </p>
                  {invalid?.reason === "not_returned" ? (
                    <EligibilityCheckRetry pending={retryPending} onRetry={onRetry} />
                  ) : invalid ? (
                    <div aria-busy={retryPending} className="flex flex-col items-start gap-2">
                      <InlineProductIssue invalid={invalid} />
                      <Button
                        type="button"
                        variant="outline"
                        size="xs"
                        disabled={retryPending}
                        onClick={onRetry}
                      >
                        {retryPending ? <Spinner data-icon="inline-start" /> : null}
                        {t(retryPending ? "products.selectionCheck.checking" : "actions.retry")}
                      </Button>
                    </div>
                  ) : null}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  className="shrink-0"
                  aria-label={
                    product.sku
                      ? t("products.removeSku", { sku: product.sku })
                      : t("products.removeAsin", { asin: product.asin })
                  }
                  onClick={() => onRemove([product.listingKey])}
                >
                  <IconTrash />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
